import { useSearchParams } from 'react-router-dom';

import { ActionButtons } from '@hcs/design-system';
import { CardTabs } from '@hcs/design-system';
import { NoContent } from '@hcs/design-system';
import { CompTypes } from '@hcs/types';
import { ReportFeatures, ReportId } from '@hcs/types';
import {
  NavigateToCompSelectionFn,
  NavigateToPropertyPreviewFn,
  NavigateToReportFn,
} from '@hcs/types';
import { combineUseQueryResult } from '@hcs/utils';

import { CompsListTypeControl } from '../../features/CompsList/CompsListControls/CompsListTypeControl';
import {
  useCompDocuments,
  useCompsListType,
  useReportPermissions,
} from '../../hooks';
import { useHcSuggestedCompsDocuments } from '../../hooks/useHcSuggestedCompsDocuments';
import { useReportConfig } from '../../hooks/useReportConfig';
import { reportFeaturesSupportedAny } from '../../utils/reportConfig.utils';
import { COMPS_LIST_TABS_ACTION_PORTAL_ID, CompsListTabs } from '../CompsList';
interface Props {
  reportId: ReportId;
  compType: CompTypes;
  isAdjustable?: boolean;
  showCompsListTypeControl?: boolean;
  navigateToReport: NavigateToReportFn;
  navigateToPropertyPreview: NavigateToPropertyPreviewFn;
  navigateToCompSelection: NavigateToCompSelectionFn;
  navigateToCompSelectionDraw: NavigateToCompSelectionFn;
}

const COMPS_LIST_TYPE_SEARCH_PARAM = 'summary--compsListType';

const REPORT_FEATURES_SALE = [
  ReportFeatures.CompTop4,
  ReportFeatures.CompsFarmView,
  ReportFeatures.CompsHcSuggested,
  ReportFeatures.AppraisalComps,
];

const REPORT_FEATURES_RENTAL = [
  ReportFeatures.RentalCompsFarmView,
  ReportFeatures.RentalCompsHcSuggested,
];

const dataHcName = 'comps-summary';
export const CompsSummarySection = ({
  reportId,
  compType,
  isAdjustable = true,
  showCompsListTypeControl,
  navigateToReport,
  navigateToPropertyPreview,
  navigateToCompSelection,
  navigateToCompSelectionDraw,
}: Props) => {
  const reportPermissionsQuery = useReportPermissions(reportId);
  const compDocumentsQuery = useCompDocuments(reportId, compType);
  const suggestedCompsQuery = useHcSuggestedCompsDocuments(reportId, compType);

  const { isLoading } = combineUseQueryResult([
    reportPermissionsQuery,
    compDocumentsQuery,
    suggestedCompsQuery,
  ]);

  const isRental = compType === 'RENTAL';
  const { data: reportPermissions } = reportPermissionsQuery;
  const { data: suggestedCompDocuments } = suggestedCompsQuery;
  const { data: reportConfig } = useReportConfig(reportId);
  const [searchParams, setSearchParams] = useSearchParams();
  const isEditable = !!reportPermissions?.isEditable;
  const dataHcEventSectionList = isRental
    ? 'Rental Comps Summary List'
    : 'Comps Summary List';
  const dataHcEventSectionMap = isRental
    ? 'Rental Comps Summary Map'
    : 'Comps Summary Map';
  /* We only want to use suggested comps if
  we have a shared report and there is no comps selected already
 */

  const compsListType = useCompsListType(reportId, compType, reportConfig);
  const compsSupported = reportFeaturesSupportedAny(
    reportConfig,
    isRental ? REPORT_FEATURES_RENTAL : REPORT_FEATURES_SALE,
  );

  if (!compsSupported) return null;

  if (!isLoading && !isEditable && suggestedCompDocuments?.length === 0) {
    return (
      <NoContent dataHcName={`${dataHcName}-no-content-no-farm-comps`}>
        No comps available for this report.
      </NoContent>
    );
  }

  return (
    <CardTabs
      dataHcName={dataHcName}
      ariaLabel="Comps Summary Section"
      searchParamId={`${dataHcName}-${compType}-outer`}
      tabs={[
        {
          tabId: 'main',
          noPadding: true,
          label: isRental
            ? compsListType === 'hcSuggestedComps'
              ? 'HC Suggested Rental Comps'
              : 'Rental Comps'
            : compsListType === 'appraisal'
              ? 'Appraisal Comps'
              : compsListType === 'hcSuggestedComps'
                ? 'HC Suggested Comps'
                : 'Sold & Active Comps',
          content: (
            <>
              {showCompsListTypeControl && (
                <ActionButtons
                  dataHcName={`${dataHcName}-actions`}
                  portalIdRender={COMPS_LIST_TABS_ACTION_PORTAL_ID}
                >
                  <CompsListTypeControl
                    reportId={reportId}
                    compsListType={compsListType}
                    excluded={{
                      compIDs: true,
                    }}
                    onChange={(v) => {
                      searchParams.set(COMPS_LIST_TYPE_SEARCH_PARAM, v);
                      setSearchParams(searchParams);
                    }}
                  />
                </ActionButtons>
              )}

              <CompsListTabs
                reportId={reportId}
                compType={compType}
                isAdjustable={isAdjustable}
                dataHcEventSectionList={dataHcEventSectionList}
                dataHcEventSectionMap={dataHcEventSectionMap}
                listViewTypes={['card', 'table', 'compare']}
                compsListType={compsListType}
                navigateToReport={navigateToReport}
                navigateToPropertyPreview={navigateToPropertyPreview}
                navigateToCompSelection={navigateToCompSelection}
                navigateToCompSelectionDraw={navigateToCompSelectionDraw}
              />
            </>
          ),
        },
      ]}
    />
  );
};
